import {
  EPartnerStatus,
  IBusinessPartnersFiltersForm,
  INewPartnerStep1,
  INewPartnerStep2,
  INewPartnerStep3,
  IPartnerContact
} from 'features/Partners/types'
import {
  GTBusinessPartner,
  GTBusinessPartnerDocument,
  GTBusinessPartnerDocumentCategory,
  GTBusinessPartnerTaxPersonType,
  GTBusinessPartnersInput,
  GTChangePartnerBankDetailsInput,
  GTChangePartnerContactInput,
  GTChangePartnerTaxInformationInput,
  GTCreatePartnerContactInput,
  GTCreatePartnerInput,
  GTPartnerBankPaymentTypeInput,
  GTPartnerTaxInformationPersonTypeInput,
  GTPartnerTypeInput,
  GTPartnerVerticalInput,
  GTUser
} from 'gql/types.generated'
import { isEmpty, isEqual } from 'lodash'
import { defineMessages } from 'react-intl'
import { getQueryString } from 'utils/route'
import { STATES } from 'utils/states'

export const EMPTY_PARTNER_CONTACT: IPartnerContact = {
  name: '',
  phone: '',
  secondaryPhone: '',
  email: '',
  secondaryEmail: ''
}

const EMPTY_BANK_DATA: INewPartnerStep2['bankData'] = {
  bankName: '',
  clabeNumber: '',
  paymentType: '',
  email: '',
  bankCoverLink: ''
}
export const getNewContacts = (contacts: INewPartnerStep2['contacts']): INewPartnerStep2['contacts'] => {
  if (contacts.length === 1 && isEqual(contacts[0], EMPTY_PARTNER_CONTACT)) {
    return []
  }
  return contacts
}
export const getNewBankData = (bankData: INewPartnerStep2['bankData']): INewPartnerStep2['bankData'] | undefined => {
  if (isEqual(bankData, EMPTY_BANK_DATA) || !bankData) {
    return undefined
  }
  return bankData
}
export const mapPartnerToStep2Values = (partner?: GTBusinessPartner): INewPartnerStep2 => {
  const contacts =
    partner?.contacts?.map((contact) => ({
      id: contact.id ?? '',
      name: contact.name,
      phone: contact.phone,
      secondaryPhone: contact.secondaryPhone ?? '',
      email: contact.email,
      secondaryEmail: contact.secondaryEmail ?? ''
    })) ?? []
  return {
    bankData: {
      bankName: partner?.bankDetails?.bankName ?? '',
      clabeNumber: partner?.bankDetails?.clabe ?? '',
      paymentType: partner?.bankDetails?.paymentType ?? '',
      email: partner?.bankDetails?.paymentEmail ?? '',
      bankCoverLink: partner?.bankDetails?.bankCoverLink ?? ''
    },
    contacts: contacts?.length > 0 ? contacts : [EMPTY_PARTNER_CONTACT]
  }
}

export const mapPartnerToStep3Values = (partner?: GTBusinessPartner): INewPartnerStep3 => {
  const orderedCategories = [
    GTBusinessPartnerDocumentCategory.OfficialId,
    GTBusinessPartnerDocumentCategory.ProofOfAddress,
    GTBusinessPartnerDocumentCategory.ConstitutiveAct,
    GTBusinessPartnerDocumentCategory.PowerOfAttorney,
    GTBusinessPartnerDocumentCategory.AccountStatementCover,
    GTBusinessPartnerDocumentCategory.ComplianceOpinion
  ]
  const documents = partner?.documents ?? []
  const partnerDocuments = [...documents].reverse()

  return {
    documents: orderedCategories.map((category) => {
      const partnerDocument = partnerDocuments.find((document) => document.category === category)
      return {
        id: partnerDocument?.id ?? '',
        category: category,
        file: partnerDocument?.url ?? '',
        filename: partnerDocument?.filename ?? ''
      }
    }),
    kam: {
      id: partner?.kamUser?.id ?? '',
      username: '',
      firstName: partner?.kamUser?.firstName ?? '',
      lastName: partner?.kamUser?.lastName ?? ''
    }
  }
}

export const mapTaxInformationFormToMutation = (values: INewPartnerStep1): GTChangePartnerTaxInformationInput => {
  return {
    personType: values.taxPersonType as GTPartnerTaxInformationPersonTypeInput,
    rfc: values.taxId,
    curp: values.curp || null,
    businessName:
      values.taxPersonType === GTBusinessPartnerTaxPersonType.LegalEntity && values.businessName
        ? values.businessName
        : null,
    companyName:
      values.taxPersonType === GTBusinessPartnerTaxPersonType.LegalEntity && values.companyName
        ? values.companyName
        : null,
    taxRegime: values.taxRegime || null,
    proofOfTaxSituation: values.proofOfTaxSituation || null,
    repseID: values.repseId || null,
    repseLink: values.repseLink || null
  }
}

export const mapPartnerContactsToMutation = (
  contacts: INewPartnerStep2['contacts']
): GTCreatePartnerContactInput[] | GTChangePartnerContactInput[] => {
  return contacts.map((contact) => ({
    id: contact.id || undefined,
    name: contact.name,
    phone: contact.phone,
    secondaryPhone: contact.secondaryPhone || null,
    email: contact.email,
    secondaryEmail: contact.secondaryEmail || null
  }))
}

export const mapPartnerBankDataToMutation = (
  bankData: INewPartnerStep2['bankData']
): GTChangePartnerBankDetailsInput => {
  return {
    bankName: bankData.bankName,
    clabe: bankData.clabeNumber,
    bankCoverLink: bankData.bankCoverLink || undefined,
    paymentEmail: bankData.email,
    paymentType: (bankData.paymentType as GTPartnerBankPaymentTypeInput) || undefined
  }
}
export const mapTaxInformationResponseToMutation = (partner: GTBusinessPartner): GTChangePartnerTaxInformationInput => {
  return {
    personType: partner?.taxInfo?.personType as unknown as GTPartnerTaxInformationPersonTypeInput,
    companyName: partner?.taxInfo?.companyName || null,
    businessName: partner?.taxInfo?.businessName || null,
    rfc: partner?.taxInfo?.rfc || '',
    curp: partner?.taxInfo?.curp || null,
    taxRegime: partner?.taxInfo?.taxRegime || null,
    proofOfTaxSituation: partner?.taxInfo?.proofOfTaxSituation || null,
    repseID: partner?.taxInfo?.repseID || null,
    repseLink: partner?.taxInfo?.repseLink || null
  }
}

export const getTaxIdDocument = (partner: GTBusinessPartner): GTBusinessPartnerDocument | undefined => {
  return partner?.documents?.find((document) => document.category === GTBusinessPartnerDocumentCategory.Rfc)
}

export const getCurpDocument = (partner: GTBusinessPartner): GTBusinessPartnerDocument | undefined => {
  return partner?.documents?.find((document) => document.category === GTBusinessPartnerDocumentCategory.Curp)
}

export const mapPartnerDetailsResponseToMutation = (partner: GTBusinessPartner): GTCreatePartnerInput => {
  return {
    name: partner.name,
    cityID: partner.city?.id || null,
    stateCode: partner.stateCode || null,
    operationalStateCodes: partner.operationalStateCodes || [],
    verticals: partner.verticals as unknown as GTPartnerVerticalInput[],
    type: partner.type as unknown as GTPartnerTypeInput
  }
}

export const mapPartnerDetailsFormToMutation = (values: INewPartnerStep1): GTCreatePartnerInput => {
  return {
    name: values.name,
    cityID: values.city?.id || null,
    stateCode: values.state?.code || null,
    operationalStateCodes: values.operationalStates.map(({ code }) => code),
    verticals: values.verticals as unknown as GTPartnerVerticalInput[],
    type: values.type as GTPartnerTypeInput
  }
}

export const mapFiltersToSearchQueryString = (filters: IBusinessPartnersFiltersForm, pageNb?: number): string => {
  return getQueryString({
    page: pageNb,
    name: encodeURIComponent(filters.name),
    status: filters.status,
    states: filters.states.map(({ code }) => code).join(','),
    email: encodeURIComponent(filters.email),
    kamId: filters.kamUser.id,
    rfc: filters.rfc,
    taxPersonType: filters.taxPersonType
  })
}
export const mapSearchToFiltersForm = (
  queries: Record<string, string | undefined>,
  kamUser?: GTUser
): IBusinessPartnersFiltersForm => {
  return {
    email: queries.email || '',
    states: STATES.filter((state) => queries.states?.split(',').includes(state.code)),
    name: queries.name || '',
    kamUser: {
      id: queries.kamId || '',
      firstName: kamUser?.firstName,
      lastName: kamUser?.lastName
    },
    status:
      queries.status === EPartnerStatus.ACTIVE
        ? EPartnerStatus.ACTIVE
        : queries.status === EPartnerStatus.INACTIVE
        ? EPartnerStatus.INACTIVE
        : '',
    rfc: queries.rfc || '',
    taxPersonType: queries.taxPersonType as GTBusinessPartnerTaxPersonType | ''
  }
}

export const TAX_PERSON_TYPE_LIST = [
  GTBusinessPartnerTaxPersonType.Individual,
  GTBusinessPartnerTaxPersonType.LegalEntity
]

export const taxPersonTypeMessages = defineMessages({
  [GTBusinessPartnerTaxPersonType.Individual]: {
    id: 'partners.taxPersonType.physical',
    defaultMessage: 'Physical Person'
  },
  [GTBusinessPartnerTaxPersonType.LegalEntity]: {
    id: 'partners.taxPersonType.moral',
    defaultMessage: 'Moral Person'
  }
})

export const mapFiltersToQueryVariables = (filters: IBusinessPartnersFiltersForm): GTBusinessPartnersInput => {
  return {
    name: filters.name || undefined,
    isActive:
      filters.status === EPartnerStatus.ACTIVE ? true : filters.status === EPartnerStatus.INACTIVE ? false : undefined,
    taxPersonType: filters.taxPersonType || undefined,
    rfc: filters.rfc || undefined,
    stateCodes: !isEmpty(filters.states) ? filters.states.map(({ code }) => code) : undefined,
    paymentEmail: filters.email || undefined,
    kamUserId: filters.kamUser.id || undefined
  }
}

export const isRepseIdRequired = (taxPersonType: GTBusinessPartnerTaxPersonType | ''): boolean => {
  return taxPersonType === GTBusinessPartnerTaxPersonType.LegalEntity
}
